import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, AlertCircle } from "lucide-react";
import { epsteinSubmit } from "../../lib/api";

const EVIDENCE_TYPES = [
  ["flight_log", "Flight log"],
  ["court_filing", "Court filing"],
  ["deposition", "Deposition"],
  ["media_report", "Media report"],
  ["photo", "Photo"],
  ["black_book", "Black book / contact record"],
  ["financial_record", "Financial / corporate record"],
  ["witness_testimony", "Witness testimony"],
  ["other", "Other"],
];

export const SubmitForm = ({ onClose }) => {
  const [form, setForm] = useState({
    source_url: "", evidence_type_guess: "media_report", claim: "",
    why_it_matters: "", names_referenced: "", suggested_relationship: "",
    quote_excerpt: "", notes: "", contact_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const url = (form.source_url || "").trim();
    if (!url || !(url.startsWith("http://") || url.startsWith("https://"))) {
      toast.error("A source URL is required to submit (must start with http:// or https://).");
      return;
    }
    if (!form.claim || form.claim.trim().length < 5) {
      toast.error("Please describe what the source shows / claims.");
      return;
    }
    setBusy(true);
    try {
      const r = await epsteinSubmit({ ...form, source_url: url });
      setDone(r);
      toast.success("Submission received and queued for editorial review.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="border border-[#D92D20]/40 bg-[#0F0F0F] p-6 text-center" data-testid="ef-submit-success">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// SUBMISSION RECEIVED</div>
        <p className="text-[#E4DFD3] mb-2">
          Submission received. It has been added to editorial review for verification and source analysis.
        </p>
        <p className="font-mono text-xs text-[#A39E93] mb-4">tracking id: <span className="text-[#E4DFD3]">{done.tracking_id}</span></p>
        <button onClick={onClose}
          className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]">
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-[#2D2A26] bg-[#0F0F0F] p-4 sm:p-5 space-y-3" data-testid="ef-submit-form">
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          // SUBMIT TO THE FILE
        </div>
        {onClose && (
          <button type="button" onClick={onClose}
            className="font-mono text-[10px] text-[#757065] hover:text-[#E4DFD3]">
            cancel
          </button>
        )}
      </div>

      <div className="flex items-start gap-2 border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2">
        <AlertCircle size={14} className="text-[#E0A526] shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#CBB892] leading-relaxed">
          Source URL is required. Submissions without a verifiable source cannot be accepted.
          Nothing appears publicly until an editor approves it.
        </p>
      </div>

      <Field label="Source URL *" testid="ef-input-url">
        <input
          required
          type="url"
          value={form.source_url}
          onChange={update("source_url")}
          placeholder="https://..."
          data-testid="ef-input-url"
          className={inputCls}
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Evidence type guess" testid="ef-select-type">
          <select value={form.evidence_type_guess} onChange={update("evidence_type_guess")}
            className={inputCls} data-testid="ef-select-type">
            {EVIDENCE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Suggested relationship to Epstein / known entity">
          <input value={form.suggested_relationship} onChange={update("suggested_relationship")}
            placeholder="e.g. listed_in_flight_logs"
            data-testid="ef-input-suggested-rel" className={inputCls} />
        </Field>
      </div>

      <Field label="What the source shows / claim *">
        <textarea required rows={3} value={form.claim} onChange={update("claim")}
          data-testid="ef-input-claim" className={inputCls} />
      </Field>

      <Field label="Why it matters">
        <textarea rows={2} value={form.why_it_matters} onChange={update("why_it_matters")}
          data-testid="ef-input-why" className={inputCls} />
      </Field>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Names / entities referenced">
          <input value={form.names_referenced} onChange={update("names_referenced")}
            placeholder="comma-separated" data-testid="ef-input-names" className={inputCls} />
        </Field>
        <Field label="Your contact email (optional)">
          <input type="email" value={form.contact_email} onChange={update("contact_email")}
            data-testid="ef-input-email" className={inputCls} />
        </Field>
      </div>

      <Field label="Quote / excerpt (optional)">
        <textarea rows={2} value={form.quote_excerpt} onChange={update("quote_excerpt")}
          data-testid="ef-input-excerpt" className={inputCls} />
      </Field>

      <Field label="Notes / page numbers / dates (optional)">
        <textarea rows={2} value={form.notes} onChange={update("notes")}
          data-testid="ef-input-notes" className={inputCls} />
      </Field>

      <button type="submit" disabled={busy}
        data-testid="ef-submit-btn"
        className="w-full flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em]">
        {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={12} />} Submit for editorial review
      </button>
    </form>
  );
};

const inputCls = "w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-xs text-[#E4DFD3] placeholder:text-[#757065]";

const Field = ({ label, children }) => (
  <label className="block">
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">{label}</div>
    {children}
  </label>
);

export default SubmitForm;
